import { Controller } from "@hotwired/stimulus"

// Drives the visual site builder shell: right-sidebar tabs (Edit / Blocks / AI),
// selecting a zone (header / page / footer) or a section, choosing where new
// sections are inserted, theme settings, and the block search. Structural and
// inline edits are server-side (button_to + turbo / inline-edit controller).
export default class extends Controller {
  static targets = [
    "editTab", "blocksTab", "aiTab",
    "editPanel", "blocksPanel", "aiPanel",
    "editEmpty", "editSelected", "editLabel", "editForm",
    "section", "zone", "settingsPanel", "pageSettingsPanel", "publishPanel",
    "addTarget", "deviceBtn", "editCanvas", "previewCanvas",
    "previewFrame", "editModeBtn", "previewModeBtn", "saveStatus"
  ]

  static values = { canvasUrl: String, messages: Object }

  // ── Live preview refresh (AI edits the draft server-side) ─────────────────
  connect() {
    this.refreshHandler = () => this.scheduleRefresh()
    window.addEventListener("builder:preview-refresh", this.refreshHandler)
    this.frameRefreshHandler = () => this.scheduleFrameRefresh()
    window.addEventListener("builder:frame-refresh", this.frameRefreshHandler)
    this.saveStartedHandler = () => this.saveStarted()
    this.saveFinishedHandler = (event) => this.saveFinished(event)
    window.addEventListener("builder:save-started", this.saveStartedHandler)
    window.addEventListener("builder:save-finished", this.saveFinishedHandler)
    this.pendingSaves = 0

    // The edit canvas reuses real storefront partials. Keep storefront forms and
    // controls inert here so editing can never navigate away or submit live data.
    this.canvasSubmitHandler = (event) => {
      if (!event.target.closest("#builder-canvas")) return
      if (event.target.closest("[data-builder-control]")) return
      event.preventDefault()
      event.stopImmediatePropagation()
      this.setSaveStatus(this.message("preview_disabled", "Preview controls are disabled while editing"), "text-amber-600")
    }
    this.element.addEventListener("submit", this.canvasSubmitHandler, true)

    this.pendingFormHandler = async (event) => {
      const form = event.target
      if (form.dataset.builderSaveReady === "true") {
        delete form.dataset.builderSaveReady
        return
      }
      if (this.pendingSaves <= 0 && !this.saveFailed) return

      event.preventDefault()
      event.stopImmediatePropagation()
      const submitter = event.submitter
      this.setSaveStatus(this.message("finishing", "Finishing pending changes…"), "text-amber-600")
      if (this.pendingSaves > 0) await (window.__builderSaveQueue || Promise.resolve())
      if (this.saveFailed) {
        this.setSaveStatus(this.message("failed", "Save failed"), "text-red-500")
        return
      }
      form.dataset.builderSaveReady = "true"
      if (submitter?.form === form && !submitter.disabled) form.requestSubmit(submitter)
      else form.requestSubmit()
    }
    this.element.addEventListener("submit", this.pendingFormHandler, true)

    this.beforeVisitHandler = async (event) => {
      if (this.resumingVisit) {
        this.resumingVisit = false
        return
      }
      if (this.pendingSaves <= 0 && !this.saveFailed) return

      event.preventDefault()
      this.setSaveStatus(this.message("finishing", "Finishing pending changes…"), "text-amber-600")
      if (this.pendingSaves > 0) await (window.__builderSaveQueue || Promise.resolve())
      if (this.saveFailed) {
        this.setSaveStatus(this.message("failed", "Save failed"), "text-red-500")
        return
      }
      this.resumingVisit = true
      window.Turbo?.visit(event.detail.url, { action: event.detail.action })
    }
    document.addEventListener("turbo:before-visit", this.beforeVisitHandler)
    this.escapeHandler = (event) => {
      if (event.key === "Escape") this.closeSlideouts()
    }
    document.addEventListener("keydown", this.escapeHandler)

    // Turbo Stream block operations replace parts of the canvas. Preserve the
    // active outline/property editor when the node still exists; if it was
    // deleted, return the sidebar to its useful empty state.
    if (this.hasEditCanvasTarget) {
      this.canvasObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.selectionRestoreFrame)
        this.selectionRestoreFrame = requestAnimationFrame(() => this.restoreSelection(true))
        this.scheduleFrameRefresh()
      })
      this.canvasObserver.observe(this.editCanvasTarget, { childList: true, subtree: true })
    }

    // The AI panel is data-turbo-permanent. Turbo can reinsert that preserved DOM
    // after this controller first connects, so enforce the saved panel both now
    // and after every Turbo render.
    this.restoreActivePanel()
    this.panelRenderHandler = () => {
      this.restoreActivePanel()
      this.schedulePanelSync()
    }
    document.addEventListener("turbo:render", this.panelRenderHandler)
    this.schedulePanelSync()
  }

  disconnect() {
    window.removeEventListener("builder:preview-refresh", this.refreshHandler)
    window.removeEventListener("builder:frame-refresh", this.frameRefreshHandler)
    window.removeEventListener("builder:save-started", this.saveStartedHandler)
    window.removeEventListener("builder:save-finished", this.saveFinishedHandler)
    this.element.removeEventListener("submit", this.canvasSubmitHandler, true)
    this.element.removeEventListener("submit", this.pendingFormHandler, true)
    document.removeEventListener("turbo:before-visit", this.beforeVisitHandler)
    document.removeEventListener("keydown", this.escapeHandler)
    document.removeEventListener("turbo:render", this.panelRenderHandler)
    this.canvasObserver?.disconnect()
    if (this.selectionRestoreFrame) cancelAnimationFrame(this.selectionRestoreFrame)
    if (this.panelSyncFrame) cancelAnimationFrame(this.panelSyncFrame)
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    if (this.frameRefreshTimer) clearTimeout(this.frameRefreshTimer)
    this.editRequestController?.abort()
  }

  // Debounce: the AI often fires several tool results in quick succession.
  scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => this.refreshPreview(), 250)
  }

  async refreshPreview() {
    if (!this.hasCanvasUrlValue) return

    try {
      const response = await fetch(this.canvasUrlValue, {
        headers: { "Accept": "text/vnd.turbo-stream.html" },
        credentials: "same-origin"
      })
      if (!response.ok) return

      window.Turbo?.renderStreamMessage(await response.text())
      requestAnimationFrame(() => this.restoreSelection())
      this.scheduleFrameRefresh()
    } catch (_error) {
      // Non-fatal: the seller can still reload manually.
    }
  }

  scheduleFrameRefresh() {
    if (!this.hasPreviewFrameTarget) return
    if (this.frameRefreshTimer) clearTimeout(this.frameRefreshTimer)
    this.frameRefreshTimer = setTimeout(() => {
      try { this.previewFrameTarget.contentWindow?.location.reload() } catch (_error) { /* same-origin draft preview only */ }
    }, 250)
  }

  // ── Device preview ─────────────────────────────────────────────────────────
  // Preview at a real iframe viewport width so normal storefront media queries,
  // fonts, forms and layout behave exactly as they do outside the builder.
  setDevice(event) {
    const btn = event.currentTarget
    const width = btn.dataset.deviceWidth || "100%"
    if (this.hasPreviewFrameTarget) this.previewFrameTarget.style.width = width
    this.deviceBtnTargets.forEach((b) => b.classList.remove("bg-white", "dark:bg-slate-800", "text-teal-600", "shadow-sm"))
    btn.classList.add("bg-white", "dark:bg-slate-800", "text-teal-600", "shadow-sm")
    this.showPreviewCanvas()
  }

  showEditCanvas() {
    if (this.hasEditCanvasTarget) this.editCanvasTarget.classList.remove("hidden")
    if (this.hasPreviewCanvasTarget) this.previewCanvasTarget.classList.add("hidden")
    this.activateModeButton(this.editModeBtnTarget, this.previewModeBtnTarget)
  }

  showPreviewCanvas() {
    if (this.hasEditCanvasTarget) this.editCanvasTarget.classList.add("hidden")
    if (this.hasPreviewCanvasTarget) this.previewCanvasTarget.classList.remove("hidden")
    if (this.hasPreviewModeBtnTarget && this.hasEditModeBtnTarget) {
      this.activateModeButton(this.previewModeBtnTarget, this.editModeBtnTarget)
    }
  }

  activateModeButton(active, inactive) {
    active.classList.add("bg-white", "dark:bg-slate-800", "text-teal-600", "shadow-sm")
    active.classList.remove("text-slate-400")
    inactive.classList.remove("bg-white", "dark:bg-slate-800", "text-teal-600", "shadow-sm")
    inactive.classList.add("text-slate-400")
  }

  // ── Tabs ─────────────────────────────────────────────────────────────────
  showEdit(remember = true) {
    this.activate(this.editTabTarget, [ this.blocksTabTarget, this.aiTabTarget ])
    this.show(this.editPanelTarget, [ this.blocksPanelTarget, this.aiPanelTarget ])
    if (remember) localStorage.setItem("builder-active-panel", "edit")
  }

  showBlocks(remember = true) {
    this.activate(this.blocksTabTarget, [ this.editTabTarget, this.aiTabTarget ])
    this.show(this.blocksPanelTarget, [ this.editPanelTarget, this.aiPanelTarget ])
    if (remember) localStorage.setItem("builder-active-panel", "blocks")
  }

  showAi(remember = true) {
    this.activate(this.aiTabTarget, [ this.editTabTarget, this.blocksTabTarget ])
    this.show(this.aiPanelTarget, [ this.editPanelTarget, this.blocksPanelTarget ])
    if (remember) localStorage.setItem("builder-active-panel", "ai")
  }

  restoreActivePanel() {
    const savedPanel = localStorage.getItem("builder-active-panel")
    if (savedPanel === "ai") this.showAi(false)
    else if (savedPanel === "edit") this.showEdit(false)
    else this.showBlocks(false)
  }

  schedulePanelSync() {
    if (this.panelSyncFrame) cancelAnimationFrame(this.panelSyncFrame)
    this.panelSyncFrame = requestAnimationFrame(() => {
      this.panelSyncFrame = null
      this.restoreActivePanel()
    })
  }

  // ── Zone / section selection → insertion target ───────────────────────────
  // Clicking an empty area of a zone (header / page / footer) targets adds there.
  selectZone(event) {
    if (event.target.closest(".builder-section") || event.target.closest("[data-builder-control]") || event.target.isContentEditable) return
    const zone = event.currentTarget
    this.setAddTarget(zone.dataset.zoneRegion, zone.dataset.zonePageId, "", zone.dataset.zoneLabel)
  }

  // Clicking a section selects it (Edit panel) and makes new sections insert
  // right after it, in its zone.
  selectSection(event) {
    if (event.target.closest("[data-builder-control]") || event.target.isContentEditable) return
    const section = event.currentTarget
    const zone = section.closest(".builder-zone")
    this.highlight(section)
    this.setAddTarget(section.dataset.zoneRegion, section.dataset.zonePageId, section.dataset.blockId, zone && zone.dataset.zoneLabel)
    this.select(section)
    this.showEdit()
  }

  // Click any block in the canvas to edit ALL its options in the Edit panel.
  // Every rendered block carries its node id (element id) + a `.block` class,
  // so we resolve the innermost block under the click.
  async selectBlock(event) {
    // Let the builder's own controls (section move/delete forms) work.
    if (event.target.closest("[data-builder-control]")) return

    // Storefront links, buttons, and form fields are inert in the editor. A click
    // selects their owning block instead of navigating or submitting live data.
    if (event.target.closest("a, button, input, select, textarea, form")) event.preventDefault()

    const el = event.target.closest(".block")
    if (!el || !el.id) return

    const zone = el.closest(".builder-zone")
    const region = (zone && zone.dataset.zoneRegion) || ""
    const pageId = (zone && zone.dataset.zonePageId) || ""
    const section = el.closest(".builder-section")

    this.highlight(el)
    this.selectedNodeId = el.id
    this.selectedRegion = region
    this.selectedPageId = pageId
    this.setAddTarget(region, pageId, section?.dataset.blockId || "", zone && zone.dataset.zoneLabel)
    await this.loadEditForm(el.id, region, pageId)
    this.showEdit()
  }

  async loadEditForm(nodeId, region, pageId) {
    if (!this.hasEditFormTarget) return

    this.editRequestController?.abort()
    this.editRequestController = new AbortController()

    const params = new URLSearchParams({ node_id: nodeId })
    if (region) params.set("region", region)
    else if (pageId) params.set("page_id", pageId)

    try {
      const response = await fetch(`/seller/builder/blocks/edit?${params.toString()}`, {
        headers: { "Accept": "text/html" },
        credentials: "same-origin",
        signal: this.editRequestController.signal
      })
      if (!response.ok) throw new Error(`${response.status}`)

      this.editFormTarget.innerHTML = await response.text()
      if (this.hasEditEmptyTarget) this.editEmptyTarget.classList.add("hidden")
      if (this.hasEditSelectedTarget) this.editSelectedTarget.classList.add("hidden")
    } catch (error) {
      if (error.name !== "AbortError") this.setSaveStatus(this.message("load_failed", "Could not load block settings"), "text-red-500")
    }
  }

  async selectTreeNode(event) {
    const button = event.currentTarget
    this.selectedNodeId = button.dataset.nodeId
    this.selectedRegion = button.dataset.region || ""
    this.selectedPageId = button.dataset.pageId || ""
    await this.loadEditForm(button.dataset.nodeId, button.dataset.region || "", button.dataset.pageId || "")
    this.showEdit()
  }

  toggleTreeNode(event) {
    const item = event.currentTarget.closest("li")
    const list = item?.querySelector(":scope > ul")
    if (!list) return
    list.classList.toggle("hidden")
    event.currentTarget.textContent = list.classList.contains("hidden") ? "▸" : "▾"
  }

  // A new section was just rendered: scroll to it, highlight it, make it the
  // anchor for the next insert.
  sectionTargetConnected(element) {
    if (element.dataset.builderNew !== "true") return

    delete element.dataset.builderNew
    const zone = element.closest(".builder-zone")
    requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "center" })
      this.highlight(element)
      this.setAddTarget(element.dataset.zoneRegion, element.dataset.zonePageId, element.dataset.blockId, zone && zone.dataset.zoneLabel)
    })
  }

  select(section) {
    const rawLabel = section.dataset.blockLabel || (section.dataset.blockType || "section").replace(/_/g, " ")
    if (this.hasEditLabelTarget) this.editLabelTarget.textContent = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1)
    if (this.hasEditEmptyTarget) this.editEmptyTarget.classList.add("hidden")
    if (this.hasEditSelectedTarget) this.editSelectedTarget.classList.remove("hidden")
  }

  highlight(section) {
    this.sectionTargets.forEach((s) => { s.style.outline = "" })
    section.style.outline = "2px solid var(--color-accent, #4f46e5)"
    section.style.outlineOffset = "-2px"
  }

  restoreSelection(clearMissing = false) {
    if (!this.selectedNodeId) return
    const selected = document.getElementById(this.selectedNodeId) || document.getElementById(`builder-block-${this.selectedNodeId}`)
    if (selected) {
      this.highlight(selected)
      return
    }
    if (!clearMissing) return

    this.selectedNodeId = null
    this.selectedRegion = null
    this.selectedPageId = null
    if (this.hasEditFormTarget) this.editFormTarget.innerHTML = ""
    if (this.hasEditEmptyTarget) this.editEmptyTarget.classList.remove("hidden")
    if (this.hasEditSelectedTarget) this.editSelectedTarget.classList.add("hidden")
  }

  // Updates the hidden fields on every add-section form + the "Adding to" label.
  setAddTarget(region, pageId, afterId, label) {
    const r = region || ""
    const panel = this.hasBlocksPanelTarget ? this.blocksPanelTarget : document
    panel.querySelectorAll('input[name="region"]').forEach((i) => { i.value = r })
    panel.querySelectorAll('input[name="page_id"]').forEach((i) => { i.value = r ? "" : (pageId || "") })
    panel.querySelectorAll('input[name="after_id"]').forEach((i) => { i.value = afterId || "" })
    if (this.hasAddTargetTarget && label) this.addTargetTarget.textContent = label
  }

  // Drag sections by their handle. Reordering is intentionally restricted to
  // siblings in the same page/header/footer zone, so a drop cannot orphan a
  // nested tree or move commerce blocks into the wrong page.
  startSectionDrag(event) {
    const section = event.currentTarget.closest(".builder-section")
    if (!section) return

    this.draggedSection = section
    section.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", section.dataset.blockId)
  }

  dragOverSection(event) {
    if (!this.draggedSection || event.currentTarget === this.draggedSection) return
    if (event.currentTarget.parentElement !== this.draggedSection.parentElement) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    const placement = event.clientY > event.currentTarget.getBoundingClientRect().top + event.currentTarget.offsetHeight / 2 ? "after" : "before"
    event.currentTarget.dataset.dropPlacement = placement
    event.currentTarget.style.boxShadow = placement === "after" ? "inset 0 -3px var(--color-accent, #0d9488)" : "inset 0 3px var(--color-accent, #0d9488)"
  }

  dragLeaveSection(event) {
    event.currentTarget.style.boxShadow = ""
    delete event.currentTarget.dataset.dropPlacement
  }

  async dropSection(event) {
    const target = event.currentTarget
    const source = this.draggedSection
    this.dragLeaveSection(event)
    if (!source || source === target || source.parentElement !== target.parentElement) return

    event.preventDefault()
    const body = new URLSearchParams({
      node_id: source.dataset.blockId,
      target_id: target.dataset.blockId,
      placement: target.dataset.dropPlacement || (event.clientY > target.getBoundingClientRect().top + target.offsetHeight / 2 ? "after" : "before")
    })
    if (source.dataset.zoneRegion) body.set("region", source.dataset.zoneRegion)
    else body.set("page_id", source.dataset.zonePageId)

    try {
      const response = await fetch("/seller/builder/blocks/move", {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Accept": "text/vnd.turbo-stream.html" },
        body
      })
      if (!response.ok) throw new Error(`${response.status}`)
      window.Turbo?.renderStreamMessage(await response.text())
    } finally {
      this.endSectionDrag()
    }
  }

  endSectionDrag() {
    if (this.draggedSection) this.draggedSection.classList.remove("opacity-50")
    this.sectionTargets.forEach((section) => { section.style.boxShadow = "" })
    this.draggedSection = null
  }

  // ── Settings + block search ────────────────────────────────────────────────
  toggleSettings(event) {
    event?.stopPropagation()
    if (!this.hasSettingsPanelTarget) return
    const willOpen = this.settingsPanelTarget.classList.contains("translate-x-full")
    this.closeSlideouts()
    if (willOpen) this.settingsPanelTarget.classList.remove("translate-x-full")
  }

  togglePageSettings(event) {
    event?.stopPropagation()
    if (!this.hasPageSettingsPanelTarget) return
    const willOpen = this.pageSettingsPanelTarget.classList.contains("translate-x-full")
    this.closeSlideouts()
    if (willOpen) this.pageSettingsPanelTarget.classList.remove("translate-x-full")
  }

  togglePublish(event) {
    event?.stopPropagation()
    if (!this.hasPublishPanelTarget) return
    const willOpen = this.publishPanelTarget.classList.contains("translate-x-full")
    this.closeSlideouts()
    if (willOpen) this.publishPanelTarget.classList.remove("translate-x-full")
  }

  closeSlideouts() {
    if (this.hasSettingsPanelTarget && !this.settingsPanelTarget.classList.contains("translate-x-full")) {
      window.dispatchEvent(new CustomEvent("theme-settings:discard"))
    }
    if (this.hasSettingsPanelTarget) this.settingsPanelTarget.classList.add("translate-x-full")
    if (this.hasPageSettingsPanelTarget) this.pageSettingsPanelTarget.classList.add("translate-x-full")
    if (this.hasPublishPanelTarget) this.publishPanelTarget.classList.add("translate-x-full")
  }

  saveStarted() {
    if (this.pendingSaves === 0) this.saveFailed = false
    this.pendingSaves += 1
    this.setSaveStatus(this.message("saving", "Saving…"), "text-slate-500")
  }

  saveFinished(event) {
    this.pendingSaves = Math.max(0, this.pendingSaves - 1)
    if (event.detail?.ok === false) {
      this.saveFailed = true
      this.setSaveStatus(this.message("failed", "Save failed"), "text-red-500")
    }
    else if (this.pendingSaves === 0) this.setSaveStatus(this.message("all_saved", "All changes saved"), "text-green-600")
  }

  setSaveStatus(message, color) {
    if (!this.hasSaveStatusTarget) return
    this.saveStatusTarget.textContent = message
    this.saveStatusTarget.classList.remove("text-slate-400", "text-slate-500", "text-green-600", "text-red-500", "text-amber-600")
    this.saveStatusTarget.classList.add(color)
  }

  message(key, fallback) {
    return this.hasMessagesValue ? (this.messagesValue[key] || fallback) : fallback
  }

  filterBlocks(event) {
    const query = event.target.value.toLowerCase().trim()
    this.element.querySelectorAll("[data-block-label]").forEach((el) => {
      el.style.display = (!query || el.dataset.blockLabel.includes(query)) ? "" : "none"
    })
    this.element.querySelectorAll("[data-block-category]").forEach((cat) => {
      const anyVisible = [ ...cat.querySelectorAll("[data-block-label]") ].some((el) => el.style.display !== "none")
      cat.style.display = anyVisible ? "" : "none"
    })
  }

  // ── helpers ────────────────────────────────────────────────────────────────
  activate(active, others) {
    active.classList.add("text-teal-600", "dark:text-teal-400", "border-b-2", "border-teal-600")
    active.classList.remove("text-slate-400", "hover:text-slate-600", "dark:hover:text-slate-200")
    others.forEach((tab) => {
      tab.classList.remove("text-teal-600", "dark:text-teal-400", "border-b-2", "border-teal-600")
      tab.classList.add("text-slate-400", "hover:text-slate-600", "dark:hover:text-slate-200")
    })
  }

  show(panel, others) {
    panel.hidden = false
    panel.setAttribute("aria-hidden", "false")
    panel.classList.remove("hidden")
    others.forEach((p) => {
      p.hidden = true
      p.setAttribute("aria-hidden", "true")
      p.classList.add("hidden")
    })
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
